import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { User, Mail, Phone, CreditCard, Calendar, Shield, CheckCircle, LogOut, Award, Ticket } from 'lucide-react';

export default function ProfilePage() {
  const { user, logout, isVerified, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="page-container pt-6 space-y-6">
      <h1 className="heading-2">Mi Perfil</h1>

      {/* User Rank Card */}
      {user?.rank && (
        <Card className="card-base border-2" style={{ borderColor: user.rank.color }} data-testid="rank-card">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Award className="w-8 h-8" style={{ color: user.rank.color }} />
                <div>
                  <p className="text-sm text-muted-foreground">Tu Rango</p>
                  <p className="text-xl font-bold" style={{ color: user.rank.color }}>{user.rank.name}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Ticket className="w-4 h-4" />
                  <span className="text-sm">Tickets comprados</span>
                </div>
                <p className="text-2xl font-bold">{user.total_tickets_purchased || 0}</p>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground text-center">
              {user.rank.key === 'invitado' && 'Compra 11+ tickets para subir a Prim@'}
              {user.rank.key === 'prima' && 'Compra 51+ tickets para subir a Herman@'}
              {user.rank.key === 'hermano' && 'Compra 101+ tickets para subir a Pro'}
              {user.rank.key === 'pro' && '¡Eres un Pro! Máximo nivel alcanzado'}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Card */}
      <Card className="card-base" data-testid="profile-card">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-cyan-600 flex items-center justify-center text-white text-2xl font-bold">
              {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
            </div>
            <div>
              <CardTitle className="text-xl">
                {user?.first_name} {user?.last_name}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {user?.rank && (
                  <span 
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                    style={{ backgroundColor: `${user.rank.color}20`, color: user.rank.color }}
                  >
                    <Award className="w-3 h-3" />
                    {user.rank.name}
                  </span>
                )}
                {isVerified ? (
                  <span className="inline-flex items-center gap-1 text-xs bg-success/10 text-success px-2 py-1 rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    Verificado
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs bg-accent/10 text-accent px-2 py-1 rounded-full">
                    <Shield className="w-3 h-3" />
                    Sin verificar
                  </span>
                )}
                {isAdmin && (
                  <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                    <Shield className="w-3 h-3" />
                    Admin
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Correo electrónico</p>
              <p className="font-medium">{user?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Teléfono</p>
              <p className="font-medium">{user?.phone}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Documento</p>
              <p className="font-medium">{user?.document_number}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Miembro desde</p>
              <p className="font-medium">{formatDate(user?.created_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Verification CTA */}
      {!isVerified && (
        <Card className="card-base bg-accent/5 border-accent/20" data-testid="verify-cta">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-accent" />
                <div>
                  <p className="font-medium">Verifica tu teléfono</p>
                  <p className="text-sm text-muted-foreground">Para comprar tickets</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/verify-phone')}
                className="rounded-full"
                data-testid="verify-btn"
              >
                Verificar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Access */}
      {isAdmin && (
        <Card className="card-base bg-primary/5 border-primary/20" data-testid="admin-cta">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-medium">Panel de Administración</p>
                  <p className="text-sm text-muted-foreground">Gestionar sorteos y usuarios</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/admin')}
                className="rounded-full"
                data-testid="admin-panel-btn"
              >
                Ir al Panel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logout */}
      <Button 
        onClick={handleLogout}
        variant="outline"
        className="w-full h-12 rounded-full text-destructive border-destructive/20 hover:bg-destructive/5"
        data-testid="logout-btn"
      >
        <LogOut className="w-5 h-5 mr-2" />
        Cerrar Sesión
      </Button>

      {/* Legal */}
      <div className="text-center text-xs text-muted-foreground space-y-2 pt-4">
        <p>TuPrima - La que alegra el día</p>
        <p>Cumple con la Ley 1581 de 2012</p>
        <p>Protección de datos personales</p>
      </div>
    </div>
  );
}
